<template>
  <section class="admin-tools">
    <details>
      <summary>
        <span>
          <strong>Admin tools</strong>
          <small>Preparation and advanced ceremony actions</small>
        </span>
        <span class="summary-meta">
          <span class="admin-status">{{ readinessLabel }}</span>
          <span class="summary-chevron" aria-hidden="true">⌄</span>
        </span>
      </summary>

      <div class="admin-body">
        <div class="current-ceremony">
          <div>
            <p class="section-label">Current ceremony</p>
            <h3>{{ currentCeremonyLabel }}</h3>
            <p>{{ readinessDescription }}</p>
          </div>
          <button
            v-if="canPrepareCurrent"
            type="button"
            class="primary-button"
            :disabled="submitting || selectedCeremoniesActive"
            @click="reviewCurrentCeremony"
          >
            Prepare this ceremony
          </button>
        </div>

        <div v-if="confirmationOpen" class="confirmation" role="alert">
          <div>
            <strong>
              Prepare {{ selectedCeremonyIds.length }} ceremon{{ selectedCeremonyIds.length === 1 ? 'y' : 'ies' }}?
            </strong>
            <p>Processing runs in the background and uses GPU capacity. Ready ceremonies are skipped.</p>
          </div>
          <div class="actions">
            <button
              type="button"
              class="primary-button"
              :disabled="submitting"
              @click="submit"
            >
              {{ submitting ? 'Starting…' : 'Start preparation' }}
            </button>
            <button
              type="button"
              class="secondary-button"
              :disabled="submitting"
              @click="confirmationOpen = false"
            >
              Cancel
            </button>
          </div>
        </div>

        <p v-if="error" class="admin-error" role="alert">{{ error }}</p>

        <div v-if="results.length" class="results">
          <div class="results-heading">
            <p class="section-label">Processing status</p>
            <button
              type="button"
              class="text-button"
              :disabled="refreshing"
              @click="refreshStatuses"
            >
              {{ refreshing ? 'Refreshing…' : 'Refresh' }}
            </button>
          </div>
          <div v-for="item in results" :key="item.ceremony_id" class="result-row">
            <span>{{ ceremonyLabel(item.ceremony_id) }}</span>
            <strong :class="`status-${item.status}`">{{ statusLabel(item) }}</strong>
          </div>
        </div>

        <details class="nested-tools">
          <summary>
            <span>
              <strong>Manage bulk processing</strong>
              <small>Prepare a bounded university season</small>
            </span>
            <span class="summary-chevron" aria-hidden="true">⌄</span>
          </summary>
          <div class="nested-body">
            <div class="processing-grid">
              <div>
                <label class="field-label">University</label>
                <div class="field-readonly">{{ selectedSlug || 'No university selected' }}</div>
              </div>
              <div>
                <label class="field-label">Season</label>
                <select
                  v-model="selectedSeasonKey"
                  class="field-input"
                  :disabled="!seasons.length"
                >
                  <option value="">Select season</option>
                  <option
                    v-for="season in seasons"
                    :key="season.key"
                    :value="season.key"
                  >
                    {{ season.label }}
                  </option>
                </select>
              </div>
            </div>

            <div v-if="seasonCeremonies.length" class="ceremony-selection">
              <div class="selection-toolbar">
                <span>{{ selectedCeremonyIds.length }} selected · maximum {{ MAX_INDEX_CEREMONIES }}</span>
                <div>
                  <button type="button" class="text-button" @click="selectSeason">
                    Select up to 25
                  </button>
                  <button
                    type="button"
                    class="text-button"
                    @click="selectedCeremonyIds = []"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <label
                v-for="ceremony in seasonCeremonies"
                :key="ceremony.id"
                class="ceremony-option"
              >
                <input
                  v-model="selectedCeremonyIds"
                  type="checkbox"
                  :value="String(ceremony.id)"
                  :disabled="selectionLimitReached && !selectedCeremonyIds.includes(String(ceremony.id))"
                />
                <span>{{ formatCeremony(ceremony) }}</span>
              </label>
            </div>

            <button
              type="button"
              class="secondary-button"
              :disabled="!selectedCeremonyIds.length || selectedCeremoniesActive"
              @click="confirmationOpen = true"
            >
              Review bulk request
            </button>
          </div>
        </details>

        <details class="nested-tools">
          <summary>
            <span>
              <strong>Advanced actions</strong>
              <small>Only use when ceremony data needs rebuilding</small>
            </span>
            <span class="summary-chevron" aria-hidden="true">⌄</span>
          </summary>
          <div class="nested-body advanced-actions">
            <div>
              <strong>{{ chapterActionLabel }}</strong>
              <p>Regenerates the transcript-derived ceremony guide. This may take several minutes.</p>
            </div>
            <button
              type="button"
              class="secondary-button"
              :disabled="!canBuildChapters || contextProcessing"
              @click="$emit('rebuild-chapters')"
            >
              {{ contextProcessing ? 'Starting…' : chapterActionLabel }}
            </button>
            <p v-if="contextMessage" class="context-message">{{ contextMessage }}</p>
            <p v-if="contextError" class="admin-error">{{ contextError }}</p>
          </div>
        </details>
      </div>
    </details>
  </section>
</template>

<script>
import { buildCeremonySeasons } from '../../../admin/ceremonySeasons.js'
import { trackAdminEvent } from '../../../admin/activity.js'
import {
  MAX_INDEX_CEREMONIES,
  getCeremonyIndexingStatus,
  startCeremonyIndexing,
} from '../../../api/ceremonyIndexing.js'

const STATUS_POLL_INTERVAL_MS = 5000
const STATUS_POLL_WINDOW_MS = 30 * 60 * 1000
const ACTIVE_STATUSES = new Set([
  'queued',
  'already_processing',
  'REQUESTED',
  'PLANNED',
  'SUBMITTED',
  'RUNNING',
])

const READINESS_COPY = {
  idle: ['No ceremony selected', 'Select a ceremony to see its preparation status.'],
  loading: ['Checking', 'Checking the latest preparation status.'],
  ready: ['Ready', 'This ceremony is searchable and ready to explore.'],
  processing: ['Preparing', 'Preparation is running in the background.'],
  not_started: ['Not prepared', 'Prepare this ceremony before searching it.'],
  no_vectors: ['Needs attention', 'Preparation finished without a searchable video.'],
  failed: ['Failed', 'The previous preparation did not complete. You can try again.'],
  unavailable: ['Unavailable', 'The ceremony status could not be loaded.'],
}

export default {
  name: 'CeremonyProcessingControls',
  props: {
    selectedSlug: {
      type: String,
      default: '',
    },
    selectedCeremonyId: {
      type: String,
      default: '',
    },
    ceremonies: {
      type: Array,
      default: () => [],
    },
    formatCeremony: {
      type: Function,
      required: true,
    },
    readiness: {
      type: String,
      default: 'idle',
    },
    canBuildChapters: {
      type: Boolean,
      default: false,
    },
    contextProcessing: {
      type: Boolean,
      default: false,
    },
    contextMessage: {
      type: String,
      default: '',
    },
    contextError: {
      type: String,
      default: '',
    },
    chapterActionLabel: {
      type: String,
      default: 'Build chapters',
    },
  },
  data() {
    return {
      MAX_INDEX_CEREMONIES,
      selectedSeasonKey: '',
      selectedCeremonyIds: [],
      confirmationOpen: false,
      submitting: false,
      refreshing: false,
      error: '',
      results: [],
      statusPollTimer: null,
      statusPollDeadline: 0,
    }
  },
  computed: {
    seasons() {
      return buildCeremonySeasons(this.ceremonies)
    },
    selectedSeason() {
      return this.seasons.find(season => season.key === this.selectedSeasonKey)
    },
    seasonCeremonies() {
      return this.selectedSeason?.ceremonies || []
    },
    selectionLimitReached() {
      return this.selectedCeremonyIds.length >= MAX_INDEX_CEREMONIES
    },
    selectedCeremoniesActive() {
      return this.selectedCeremonyIds.some(ceremonyId =>
        this.results.some(
          item =>
            item.ceremony_id === ceremonyId && ACTIVE_STATUSES.has(item.status)
        )
      )
    },
    currentCeremony() {
      return this.ceremonies.find(
        ceremony => String(ceremony.id) === this.selectedCeremonyId
      )
    },
    currentCeremonyLabel() {
      return this.currentCeremony
        ? this.formatCeremony(this.currentCeremony)
        : 'No ceremony selected'
    },
    readinessLabel() {
      return (READINESS_COPY[this.readiness] || READINESS_COPY.unavailable)[0]
    },
    readinessDescription() {
      return (READINESS_COPY[this.readiness] || READINESS_COPY.unavailable)[1]
    },
    canPrepareCurrent() {
      return Boolean(
        this.selectedCeremonyId
        && ['not_started', 'no_vectors', 'failed'].includes(this.readiness)
      )
    },
  },
  watch: {
    seasons: {
      immediate: true,
      handler(seasons) {
        if (!seasons.some(season => season.key === this.selectedSeasonKey)) {
          this.selectedSeasonKey = seasons[0]?.key || ''
        }
      },
    },
    selectedSeasonKey() {
      this.selectedCeremonyIds = []
      this.confirmationOpen = false
    },
    selectedSlug() {
      this.clearStatusPolling()
      this.results = []
      this.error = ''
    },
    selectedCeremonyId() {
      this.clearStatusPolling()
      this.selectedCeremonyIds = []
      this.confirmationOpen = false
      this.results = []
      this.error = ''
    },
  },
  beforeDestroy() {
    this.clearStatusPolling()
  },
  methods: {
    reviewCurrentCeremony() {
      this.selectedCeremonyIds = [this.selectedCeremonyId]
      this.confirmationOpen = true
    },
    selectSeason() {
      this.selectedCeremonyIds = this.seasonCeremonies
        .slice(0, MAX_INDEX_CEREMONIES)
        .map(ceremony => String(ceremony.id))
    },
    ceremonyLabel(ceremonyId) {
      const ceremony = this.ceremonies.find(row => String(row.id) === ceremonyId)
      return ceremony ? this.formatCeremony(ceremony) : ceremonyId
    },
    statusLabel(item) {
      const failures = {
        DURATION_UNAVAILABLE: 'Failed: source duration unavailable',
        START_EXECUTION_FAILED: 'Failed to start',
        BATCH_SUBMIT_FAILED: 'Failed to start processing',
        PROCESSING_STALLED: 'Processing stalled — try again',
      }
      if (item.status === 'FAILED' && failures[item.error_code]) {
        return failures[item.error_code]
      }
      if (item.status === 'error') {
        const reasons = {
          service_not_configured: 'Processing is not configured',
          source_check_failed: 'Could not verify source footage',
          dispatch_failed: 'Could not start processing',
        }
        return reasons[item.reason] || 'Could not queue'
      }
      const labels = {
        queued: 'Queued',
        already_ready: 'Ready',
        already_processing: 'Processing',
        source_not_ready: 'Source not ready',
        not_found: 'Not found',
        NOT_STARTED: 'Not started',
        REQUESTED: 'Queued',
        PLANNED: 'Planning',
        SUBMITTED: 'Submitted',
        RUNNING: 'Processing',
        SUCCEEDED: item.ready ? 'Ready' : 'Finished without vectors',
        FAILED: 'Failed',
      }
      return labels[item.status] || item.status
    },
    clearStatusPolling() {
      if (this.statusPollTimer) {
        clearTimeout(this.statusPollTimer)
        this.statusPollTimer = null
      }
      this.statusPollDeadline = 0
    },
    hasActiveResults() {
      return this.results.some(item => ACTIVE_STATUSES.has(item.status))
    },
    readinessForResult(item) {
      if (!item) return ''
      if (ACTIVE_STATUSES.has(item.status)) return 'processing'
      if (item.status === 'already_ready') return 'ready'
      if (item.status === 'FAILED' || item.status === 'error') return 'failed'
      if (item.status === 'SUCCEEDED') return item.ready ? 'ready' : 'no_vectors'
      return ''
    },
    emitCurrentReadiness(results) {
      const current = results.find(
        item => item.ceremony_id === this.selectedCeremonyId
      )
      const readiness = this.readinessForResult(current)
      if (readiness && readiness !== this.readiness) {
        this.$emit('current-readiness', readiness)
      }
    },
    scheduleStatusRefresh() {
      if (!this.hasActiveResults() || Date.now() >= this.statusPollDeadline) {
        this.clearStatusPolling()
        return
      }
      if (this.statusPollTimer) clearTimeout(this.statusPollTimer)
      this.statusPollTimer = setTimeout(() => {
        this.statusPollTimer = null
        this.refreshStatuses()
      }, STATUS_POLL_INTERVAL_MS)
    },
    async submit() {
      if (!this.selectedCeremonyIds.length || this.submitting) return
      this.submitting = true
      this.error = ''
      try {
        const response = await startCeremonyIndexing(this.selectedCeremonyIds)
        this.results = response.data?.results || []
        this.emitCurrentReadiness(this.results)
        this.confirmationOpen = false
        this.statusPollDeadline = Date.now() + STATUS_POLL_WINDOW_MS
        this.scheduleStatusRefresh()
        trackAdminEvent('embedding_processing_requested', {
          toolKey: 'ceremony_intelligence',
          metadata: {
            universitySlug: this.selectedSlug,
            requestScope: response.data?.request_scope,
            requestedCount: this.selectedCeremonyIds.length,
            queuedCount: response.data?.counts?.queued || 0,
          },
        })
      } catch (error) {
        this.error = error?.response?.data?.error || 'Processing could not be queued.'
      } finally {
        this.submitting = false
      }
    },
    async refreshStatuses() {
      if (!this.results.length || this.refreshing) return
      this.refreshing = true
      this.error = ''
      const refreshed = await Promise.all(
        this.results.map(async item => {
          try {
            const response = await getCeremonyIndexingStatus(item.ceremony_id)
            if (
              response.data?.job?.status === 'NOT_STARTED'
              && ACTIVE_STATUSES.has(item.status)
            ) {
              return item
            }
            return {
              ceremony_id: response.data.ceremony_id,
              ...(response.data.job || {}),
            }
          } catch (_) {
            return { ...item, refreshFailed: true }
          }
        })
      )
      this.results = refreshed
      this.emitCurrentReadiness(refreshed)
      if (refreshed.some(item => item.refreshFailed)) {
        this.error = 'Some processing statuses could not be refreshed.'
      }
      this.refreshing = false
      if (!this.statusPollDeadline) {
        this.statusPollDeadline = Date.now() + STATUS_POLL_WINDOW_MS
      }
      this.scheduleStatusRefresh()
    },
  },
}
</script>

<style scoped>
.admin-tools {
  margin-top: 28px;
  border-top: 1px solid #e6e9ef;
  padding-top: 18px;
}

.admin-tools > details {
  border: 1px solid #e1e5ec;
  border-radius: 14px;
  background: #fafbfc;
}

summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 15px 17px;
  cursor: pointer;
  list-style: none;
}

summary::-webkit-details-marker {
  display: none;
}

.summary-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-chevron {
  color: #7c8494;
  font-size: 1rem;
  line-height: 1;
  transition: transform 150ms ease;
}

.admin-tools details[open] > summary .summary-chevron {
  transform: rotate(180deg);
}

summary span:first-child {
  display: grid;
  gap: 2px;
}

summary strong {
  color: #344054;
  font-size: 0.82rem;
}

summary small {
  color: #7c8494;
  font-size: 0.7rem;
}

.admin-status {
  border-radius: 999px;
  padding: 5px 9px;
  background: #eef0f4;
  color: #475467;
  font-size: 0.68rem;
  font-weight: 700;
}

.admin-body {
  display: grid;
  gap: 12px;
  padding: 0 14px 14px;
}

.current-ceremony,
.confirmation,
.advanced-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  border-radius: 11px;
  padding: 15px;
  background: #fff;
}

.section-label,
.current-ceremony h3,
.current-ceremony p,
.confirmation p,
.advanced-actions p {
  margin: 0;
}

.section-label {
  color: #7c3aed;
  font-size: 0.66rem;
  font-weight: 750;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.current-ceremony h3 {
  margin-top: 4px;
  color: #1d2939;
  font-size: 0.86rem;
}

.current-ceremony p,
.confirmation p,
.advanced-actions p {
  margin-top: 4px;
  color: #667085;
  font-size: 0.72rem;
}

.actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.primary-button,
.secondary-button {
  min-height: 38px;
  border-radius: 8px;
  padding: 0 13px;
  font-size: 0.74rem;
  font-weight: 720;
}

.primary-button {
  border: 1px solid #17132f;
  background: #17132f;
  color: #fff;
}

.secondary-button {
  border: 1px solid #cfd5df;
  background: #fff;
  color: #344054;
}

.primary-button:disabled,
.secondary-button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.results {
  border-radius: 11px;
  padding: 13px 15px;
  background: #fff;
}

.results-heading,
.result-row,
.selection-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.result-row {
  padding: 9px 0;
  border-top: 1px solid #edf0f4;
  color: #475467;
  font-size: 0.72rem;
}

.result-row strong {
  text-align: right;
}

.status-SUCCEEDED,
.status-already_ready {
  color: #067647;
}

.status-FAILED,
.status-error {
  color: #b42318;
}

.status-queued,
.status-already_processing,
.status-REQUESTED,
.status-PLANNED,
.status-SUBMITTED,
.status-RUNNING {
  color: #6941c6;
}

.text-button {
  border: 0;
  padding: 4px 6px;
  background: transparent;
  color: #6941c6;
  font-size: 0.7rem;
  font-weight: 700;
}

.nested-tools {
  border: 1px solid #e4e8ef;
  border-radius: 11px;
  background: #fff;
}

.nested-tools summary {
  padding: 13px 15px;
}

.nested-body {
  padding: 0 15px 15px;
  border-top: 1px solid #edf0f4;
}

.processing-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding-top: 14px;
}

.field-label {
  display: block;
  margin-bottom: 5px;
  color: #667085;
  font-size: 0.7rem;
}

.field-input,
.field-readonly {
  width: 100%;
  min-height: 40px;
  border: 1px solid #d7dce5;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  color: #344054;
  font-size: 0.76rem;
}

.field-readonly {
  display: flex;
  align-items: center;
  background: #f8f9fb;
}

.ceremony-selection {
  margin: 12px 0;
}

.selection-toolbar {
  margin-bottom: 6px;
  color: #667085;
  font-size: 0.68rem;
}

.ceremony-option {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 2px;
  border-top: 1px solid #edf0f4;
  color: #475467;
  font-size: 0.72rem;
}

.advanced-actions {
  align-items: flex-start;
  padding: 14px 0 0;
}

.context-message,
.admin-error {
  margin: 0;
  color: #667085;
  font-size: 0.72rem;
}

.admin-error {
  color: #b42318;
}

@media (max-width: 767px) {
  .current-ceremony,
  .confirmation,
  .advanced-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .actions,
  .primary-button,
  .secondary-button {
    width: 100%;
  }

  .processing-grid {
    grid-template-columns: 1fr;
  }
}
</style>
